import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeWorkGroup } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useActiveUsers } from '../hooks/useActiveUsers';
import ChangeWorkGroupInternalChecklistEditor from './ChangeWorkGroupInternalChecklistEditor';
import ChangeWorkGroupIncidentModal, {
  type IncidentGroupDraft,
} from './ChangeWorkGroupIncidentModal';
import ChangeWorkGroupPeopleModal from './ChangeWorkGroupPeopleModal';
import ChangeWorkGroupsImportExportModal from './ChangeWorkGroupsImportExportModal';
import { filterWorkGroupsBySearch } from '../changeWorkGroupListFilter';
import { fetchAllActiveUsers } from '../../../utils/searchUsers';

type OpenModal = 'checklist' | 'incident' | 'people' | 'edit' | null;

const checklistLabel = (item: ChangeWorkGroup): string => {
  const dispatching = (item.internal_templates || []).filter((tpl) => tpl.show_on_dispatch);
  const legacy = !(item.internal_templates || []).length && item.auto_subtask_on_normal;
  if (!dispatching.length && !legacy) return 'Não dispara';
  const model = dispatching[0]?.name || item.internal_subtask_template_name;
  const fields = item.checklist_field_count ?? 0;
  const base = model ? model : fields ? `${fields} campo(s) no grupo` : 'Sem campos';
  return dispatching.length > 1 ? `${dispatching.length} internas no disparo` : base;
};

const incidentLabel = (item: ChangeWorkGroup, queueName?: string): string => {
  if (!item.open_in_incidents) return 'Não abre';
  return queueName ? `Abre chamado · ${queueName}` : 'Abre chamado';
};

const peopleEntries = (item: ChangeWorkGroup): Array<{ key: string; name: string; isManager?: boolean }> => [
  ...(item.members || []).map((member) => ({
    key: `u-${member.user_id}`,
    name: member.name || 'Sem nome',
    isManager: Boolean(member.is_manager),
  })),
  ...(item.contacts || []).map((contact) => ({
    key: `c-${contact.id}`,
    name: contact.name,
  })),
];

const ChangeWorkGroupsPanel: React.FC = () => {
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const canManage = isChangeManager(user);
  const { users } = useActiveUsers({ limit: 80, enabled: canManage });
  const [items, setItems] = useState<ChangeWorkGroup[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newAreaName, setNewAreaName] = useState('');
  const [openGroupId, setOpenGroupId] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingIncident, setSavingIncident] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [incidentTargets, setIncidentTargets] = useState<{
    groups: Array<{ id: number; name: string }>;
    workflows: Array<{ id: number; name: string; group_ids: number[] }>;
  }>({ groups: [], workflows: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [areas, targets] = await Promise.all([
        changesApi.listWorkGroups(),
        changesApi.listIncidentTargets(),
      ]);
      setError(null);
      setItems(areas.items || []);
      setIncidentTargets(targets);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar os grupos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleExportGroups = async () => {
    setExporting(true);
    try {
      const data = await changesApi.exportWorkGroups();
      const jsonContent = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `grupos_mudancas_export_${new Date().toISOString().split('T')[0]}.json`,
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      showAlert({
        title: 'Erro ao exportar',
        message: getApiErrorMessage(err, 'Não foi possível exportar os grupos.'),
        variant: 'danger',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleCreateArea = async () => {
    const name = newAreaName.trim();
    if (name.length < 2) {
      showAlert({
        title: 'Nome do grupo',
        message: 'Informe um nome com ao menos 2 caracteres.',
        variant: 'warning',
      });
      return;
    }
    try {
      await changesApi.createWorkGroup({ name });
      setNewAreaName('');
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível criar o grupo.'),
        variant: 'danger',
      });
    }
  };

  const handleToggle = async (item: ChangeWorkGroup) => {
    try {
      await changesApi.setWorkGroupActive(item.group_id, !item.is_active);
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o grupo.'),
        variant: 'danger',
      });
    }
  };

  const handleToggleAuto = async (item: ChangeWorkGroup) => {
    try {
      await changesApi.updateWorkGroup(item.group_id, {
        auto_subtask_on_normal: !item.auto_subtask_on_normal,
      });
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o grupo.'),
        variant: 'danger',
      });
    }
  };

  const handleToggleThread = async (item: ChangeWorkGroup) => {
    try {
      await changesApi.updateWorkGroup(item.group_id, {
        auto_subtask_allow_thread: !item.auto_subtask_allow_thread,
      });
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o grupo.'),
        variant: 'danger',
      });
    }
  };

  const handleTemplateIdsChange = async (groupId: number, templateIds: number[]) => {
    try {
      await changesApi.updateWorkGroup(groupId, {
        internal_subtask_template_ids: templateIds,
      });
      await load();
      setOpenGroupId(groupId);
      setOpenModal('checklist');
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível vincular as subtarefas internas.'),
        variant: 'danger',
      });
    }
  };

  const closeModal = () => {
    setOpenModal(null);
    setOpenGroupId(null);
  };

  const handleSaveIncident = async (payload: IncidentGroupDraft) => {
    if (!openGroupId) return;
    setSavingIncident(true);
    try {
      await changesApi.updateWorkGroup(openGroupId, {
        open_in_incidents: payload.open_in_incidents,
        incidents_group_id: payload.incidents_group_id,
        incidents_workflow_id: payload.incidents_workflow_id,
        prompt_on_create: payload.prompt_on_create,
        incident_attachment_allowed: payload.incident_attachment_allowed,
        incident_title_default: payload.incident_title_default,
        incident_description_default: payload.incident_description_default,
        incident_urgency_default: payload.incident_urgency_default || null,
        incident_custom_field_defaults: payload.incident_custom_field_defaults || {},
        incident_kind: payload.incident_kind || null,
      });
      await load();
      closeModal();
    } catch (err: unknown) {
      showAlert({
        title: 'Incidente',
        message: getApiErrorMessage(
          err,
          'Ligue o chamado só depois de escolher o grupo de incidentes.',
        ),
        variant: 'danger',
      });
    } finally {
      setSavingIncident(false);
    }
  };

  const handleAddMember = async (
    groupId: number,
    userId: number,
    isManager = false,
  ): Promise<boolean> => {
    if (!userId) return false;
    try {
      await changesApi.addWorkGroupMember(groupId, userId, { is_manager: isManager });
      await load();
      return true;
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível incluir o membro.'),
        variant: 'danger',
      });
      return false;
    }
  };

  const handleSetMemberManager = async (
    groupId: number,
    userId: number,
    isManager: boolean,
  ): Promise<boolean> => {
    try {
      await changesApi.setWorkGroupMemberManager(groupId, userId, isManager);
      await load();
      return true;
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o gestor do grupo.'),
        variant: 'danger',
      });
      return false;
    }
  };

  const handleSetMemberImmediateManager = async (
    groupId: number,
    userId: number,
    immediateManagerUserId: number | null,
  ): Promise<boolean> => {
    try {
      await changesApi.setWorkGroupMemberImmediateManager(
        groupId,
        userId,
        immediateManagerUserId,
      );
      await load();
      return true;
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o gerente imediato.'),
        variant: 'danger',
      });
      return false;
    }
  };

  const handleAddPerson = async (groupId: number, name: string, email: string): Promise<boolean> => {
    if (name.trim().length < 2 || !email.trim()) {
      showAlert({
        title: 'Nome e e-mail',
        message: 'Informe nome e e-mail para incluir a pessoa no grupo.',
        variant: 'warning',
      });
      return false;
    }
    try {
      await changesApi.addWorkGroupPerson(groupId, { name: name.trim(), email: email.trim() });
      await load();
      return true;
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível incluir a pessoa.'),
        variant: 'danger',
      });
      return false;
    }
  };

  const handleRemoveMember = async (groupId: number, userId: number, _name: string): Promise<boolean> => {
    try {
      await changesApi.removeWorkGroupMember(groupId, userId);
      await load();
      return true;
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível remover o membro.'),
        variant: 'danger',
      });
      return false;
    }
  };

  const openGroup = items.find((item) => item.group_id === openGroupId) || null;
  const queueNameById = useMemo(
    () => Object.fromEntries(incidentTargets.groups.map((item) => [item.id, item.name])),
    [incidentTargets.groups],
  );
  const listLabels = useMemo(
    () => ({
      checklist: checklistLabel,
      incident: incidentLabel,
      peopleNames: (item: ChangeWorkGroup) =>
        peopleEntries(item)
          .map((person) => person.name)
          .join(' '),
    }),
    [],
  );
  const filteredItems = useMemo(
    () => filterWorkGroupsBySearch(items, searchQuery, queueNameById, listLabels),
    [items, searchQuery, queueNameById, listLabels],
  );
  const openFor = (item: ChangeWorkGroup, modal: Exclude<OpenModal, null>) => {
    setOpenGroupId(item.group_id);
    setOpenModal(modal);
    if (modal === 'edit') {
      setEditName(item.name || '');
      setEditDescription(item.description || '');
    }
  };

  const handleSaveGroupDetails = async () => {
    if (!openGroupId) return;
    const name = editName.trim();
    if (name.length < 2) {
      showAlert({
        title: 'Nome do grupo',
        message: 'Informe um nome com ao menos 2 caracteres.',
        variant: 'warning',
      });
      return;
    }
    setSavingEdit(true);
    try {
      await changesApi.updateWorkGroup(openGroupId, {
        name,
        description: editDescription.trim() || null,
      });
      await load();
      closeModal();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível salvar o grupo.'),
        variant: 'danger',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddAllSystemUsers = async (
    groupId: number,
    isManager: boolean,
    existingMemberIds: number[],
  ): Promise<{ added: number; skipped: number }> => {
    if (!groupId) return { added: 0, skipped: 0 };
    const memberSet = new Set(existingMemberIds);
    try {
      const allUsers = await fetchAllActiveUsers(localStorage.getItem('token'));
      const toAdd = allUsers.filter((entry) => !memberSet.has(entry.id));
      let added = 0;
      for (const entry of toAdd) {
        try {
          await changesApi.addWorkGroupMember(groupId, entry.id, { is_manager: isManager });
          added += 1;
          memberSet.add(entry.id);
        } catch {
          // Continua com os demais (ex.: usuário já incluído em corrida paralela).
        }
      }
      await load();
      return { added, skipped: toAdd.length - added };
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível carregar os usuários do sistema.'),
        variant: 'danger',
      });
      return { added: 0, skipped: 0 };
    }
  };

  return (
    <div>
      <p className="text-muted small">
        Cadastro só da Gestão de Mudanças. Incluir ou tirar pessoas nesta aba não altera
        incidentes, compras nem PMO. Use os botões de cada linha: Interna vincula a subtarefa
        do cadastro e liga o disparo na criação; Externa configura o chamado que vai para o
        módulo de Incidentes.
      </p>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {canManage ? (
        <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
          <Button
            variant="outline-primary"
            onClick={() => void handleExportGroups()}
            disabled={exporting}
          >
            {exporting ? 'Exportando...' : 'Exportar grupos'}
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowImportModal(true)}>
            Importar grupos
          </Button>
        </div>
      ) : null}
      <Row className="g-2 mb-3 align-items-center">
        <Col md={6} lg={5}>
          <Form.Control
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Pesquisar grupo, pessoa ou chamado no Incidente…"
            aria-label="Pesquisar grupos"
            disabled={loading && items.length === 0}
          />
        </Col>
        {searchQuery.trim() && !loading ? (
          <Col md="auto" className="small text-muted" aria-live="polite">
            {filteredItems.length} de {items.length} grupo(s)
          </Col>
        ) : null}
      </Row>
      {canManage ? (
        <Row className="g-2 mb-3 align-items-center">
          <Col md={5}>
            <Form.Control
              value={newAreaName}
              onChange={(e) => setNewAreaName(e.target.value)}
              placeholder="Novo grupo (ex.: GSL - Arquitetura)"
              aria-label="Nome do novo grupo"
            />
          </Col>
          <Col md="auto">
            <Button onClick={() => void handleCreateArea()}>Criar grupo</Button>
          </Col>
        </Row>
      ) : null}
      {loading ? <div>Carregando...</div> : null}
      <Card>
        <Card.Body>
          {!loading && items.length > 0 && filteredItems.length === 0 ? (
            <p className="text-muted small mb-3">Nenhum grupo corresponde à pesquisa.</p>
          ) : null}
          <Table responsive hover>
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Status</th>
                <th>Interna</th>
                <th>Externa</th>
                <th>Pessoas</th>
                {canManage ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const people = peopleEntries(item);
                return (
                <tr key={item.group_id}>
                  <td>
                    <strong>{item.name}</strong>
                    {item.description ? <div className="small text-muted">{item.description}</div> : null}
                  </td>
                  <td>
                    <Badge bg={item.is_active ? 'success' : 'secondary'}>
                      {item.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="small text-muted">{checklistLabel(item)}</td>
                  <td className="small text-muted">
                    {incidentLabel(
                      item,
                      item.incidents_group_id
                        ? queueNameById[item.incidents_group_id]
                        : undefined,
                    )}
                  </td>
                  <td className="small">
                    {people.length ? (
                      <ul className="list-unstyled mb-0">
                        {people.map((person) => (
                          <li key={person.key}>
                            {person.name}
                            {person.isManager ? (
                              <Badge bg="primary" pill className="ms-1">
                                Gestor
                              </Badge>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted">Ninguém</span>
                    )}
                  </td>
                  {canManage ? (
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openFor(item, 'checklist')}
                        >
                          Interna
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openFor(item, 'incident')}
                        >
                          Externa
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openFor(item, 'edit')}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openFor(item, 'people')}
                        >
                          Pessoas
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => void handleToggle(item)}
                        >
                          {item.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Modal
        show={openModal === 'checklist' && Boolean(openGroup)}
        onHide={closeModal}
        backdrop="static"
        keyboard={false}
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Internas — {openGroup?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {openGroup ? (
            <ChangeWorkGroupInternalChecklistEditor
              group={openGroup}
              onToggleAuto={() => handleToggleAuto(openGroup)}
              onToggleThread={() => handleToggleThread(openGroup)}
              onTemplateIdsChange={(templateIds) =>
                handleTemplateIdsChange(openGroup.group_id, templateIds)
              }
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>Fechar</Button>
        </Modal.Footer>
      </Modal>
      <ChangeWorkGroupIncidentModal
        group={openModal === 'incident' ? openGroup : null}
        targets={incidentTargets}
        saving={savingIncident}
        onHide={closeModal}
        onSave={handleSaveIncident}
        users={users}
      />
      <Modal
        show={openModal === 'edit' && Boolean(openGroup)}
        onHide={closeModal}
        backdrop="static"
        keyboard={false}
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">Editar grupo — {openGroup?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              aria-label={`Nome do grupo ${openGroup?.name || ''}`}
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Descrição (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              aria-label={`Descrição do grupo ${openGroup?.name || ''}`}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={savingEdit}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSaveGroupDetails()} disabled={savingEdit}>
            {savingEdit ? 'Salvando…' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
      <ChangeWorkGroupPeopleModal
        group={openModal === 'people' ? openGroup : null}
        users={users}
        canManage={canManage}
        onHide={closeModal}
        onAddAllSystemUsers={(isManager) =>
          handleAddAllSystemUsers(
            openGroup?.group_id || 0,
            isManager,
            (openGroup?.members || []).map((member) => member.user_id),
          )
        }
        onAddMember={(userId, isManager) =>
          handleAddMember(openGroup?.group_id || 0, userId, isManager)
        }
        onSetMemberManager={(userId, isManager) =>
          handleSetMemberManager(openGroup?.group_id || 0, userId, isManager)
        }
        onSetMemberImmediateManager={(userId, managerId) =>
          handleSetMemberImmediateManager(openGroup?.group_id || 0, userId, managerId)
        }
        onAddPerson={(name, email) => handleAddPerson(openGroup?.group_id || 0, name, email)}
        onRemoveMember={(userId, name) =>
          handleRemoveMember(openGroup?.group_id || 0, userId, name)
        }
      />
      <ChangeWorkGroupsImportExportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImported={() => {
          setShowImportModal(false);
          void load();
        }}
      />
    </div>
  );
};

export default ChangeWorkGroupsPanel;
